import { linhaSchema, postoSchema, setorSchema } from '@projetive-op/domain'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, createFileRoute } from '@tanstack/react-router'
import { TabletSmartphone } from 'lucide-react'
import { useState } from 'react'

import {
  BarraTitulo,
  BotaoEditar,
  Formulario,
  RodapeGaveta,
  TituloSecao,
  aoSubmeter,
  useGavetaCadastro,
} from '@/components/cadastros/base'
import { TabletsDoPosto } from '@/components/gestao/tablets-do-posto'
import { Button } from '@/components/ui/button'
import { Campo, Checkbox, Input, Select } from '@/components/ui/campo'
import { Gaveta } from '@/components/ui/gaveta'
import { Ativo, Cabecalho, Carregando, Linha, Tabela, Td, Th, Vazio } from '@/components/ui/tabela'
import { useTRPC } from '@/lib/trpc'

export const Route = createFileRoute('/gestao/fabrica')({ component: Fabrica })

function Fabrica() {
  return (
    <>
      <BarraTitulo
        titulo="Fábrica"
        descricao="Linhas agrupam setores; setores agrupam postos equivalentes (qualquer posto do setor puxa a ficha da etapa)."
      />
      {/* Linhas e setores lado a lado; postos (a tabela larga) embaixo até caber em três colunas. */}
      <div className="grid items-start gap-8 min-[1680px]:grid-cols-[minmax(0,0.8fr)_minmax(0,1.1fr)_minmax(0,1.7fr)] lg:grid-cols-[minmax(0,1fr)_minmax(0,1.5fr)]">
        <Linhas />
        <Setores />
        <Postos />
      </div>
    </>
  )
}

interface LinhaItem {
  id: string
  nome: string
  ordem: number
  ativo: boolean
}

function Linhas() {
  const trpc = useTRPC()
  const qc = useQueryClient()
  const lista = useQuery(trpc.cadastros.linhas.listar.queryOptions())
  const g = useGavetaCadastro<LinhaItem>()
  const invalidar = () => qc.invalidateQueries({ queryKey: trpc.cadastros.pathKey() })
  const criar = useMutation(
    trpc.cadastros.linhas.criar.mutationOptions({
      onSuccess: () => {
        void invalidar()
        g.fechar()
      },
    }),
  )
  const atualizar = useMutation(
    trpc.cadastros.linhas.atualizar.mutationOptions({
      onSuccess: () => {
        void invalidar()
        g.fechar()
      },
    }),
  )

  const submit = aoSubmeter({
    schema: linhaSchema,
    numericos: ['ordem'],
    booleanos: ['ativo'],
    setErros: g.setErros,
    sucesso: (d) => `Linha ${d.nome} salva.`,
    onValido: (dados) =>
      g.item ? atualizar.mutateAsync({ id: g.item.id, ...dados }) : criar.mutateAsync(dados),
  })

  return (
    <section className="min-w-0">
      <TituloSecao titulo="Linhas" onNovo={g.abrirNovo} rotuloNovo="Nova" />
      <Tabela>
        <Cabecalho>
          <Th>Nome</Th>
          <Th>Setores</Th>
          <Th />
        </Cabecalho>
        <tbody>
          {lista.data?.map((l) => (
            <Linha key={l.id}>
              <Td className="font-medium">
                {l.nome} {!l.ativo && <Ativo ativo={false} />}
              </Td>
              <Td numerico>{l._count.setores}</Td>
              <Td className="text-right">
                <BotaoEditar rotulo={l.nome} onClick={() => g.abrirEditar(l)} />
              </Td>
            </Linha>
          ))}
          {lista.isPending && <Carregando colunas={3} />}
          {lista.data?.length === 0 && <Vazio colunas={3}>Nenhuma linha.</Vazio>}
        </tbody>
      </Tabela>
      <Gaveta
        aberta={g.aberta}
        titulo={g.item ? 'Editar linha' : 'Nova linha'}
        onFechar={g.fechar}
        rodape={
          <RodapeGaveta
            form="form-linha"
            salvando={criar.isPending || atualizar.isPending}
            onCancelar={g.fechar}
          />
        }
      >
        <Formulario id="form-linha" onSubmit={submit} erros={g.erros} onLimparErro={g.limparErro}>
          <Campo label="Nome" erro={g.erros.nome}>
            {(p) => <Input {...p} name="nome" defaultValue={g.item?.nome} autoFocus />}
          </Campo>
          <Campo label="Ordem de exibição" erro={g.erros.ordem}>
            {(p) => (
              <Input {...p} name="ordem" type="number" min={0} defaultValue={g.item?.ordem ?? 0} />
            )}
          </Campo>
          <Checkbox name="ativo" label="Ativa" defaultChecked={g.item?.ativo ?? true} />
        </Formulario>
      </Gaveta>
    </section>
  )
}

interface SetorItem {
  id: string
  nome: string
  linhaId: string | null
  ordem: number
  ativo: boolean
}

function Setores() {
  const trpc = useTRPC()
  const qc = useQueryClient()
  const lista = useQuery(trpc.cadastros.setores.listar.queryOptions())
  const linhas = useQuery(trpc.cadastros.linhas.listar.queryOptions())
  const g = useGavetaCadastro<SetorItem>()
  const invalidar = () => qc.invalidateQueries({ queryKey: trpc.cadastros.pathKey() })
  const criar = useMutation(
    trpc.cadastros.setores.criar.mutationOptions({
      onSuccess: () => {
        void invalidar()
        g.fechar()
      },
    }),
  )
  const atualizar = useMutation(
    trpc.cadastros.setores.atualizar.mutationOptions({
      onSuccess: () => {
        void invalidar()
        g.fechar()
      },
    }),
  )

  const submit = aoSubmeter({
    schema: setorSchema,
    numericos: ['ordem'],
    opcionais: ['linhaId'],
    booleanos: ['ativo'],
    setErros: g.setErros,
    sucesso: (d) => `Setor ${d.nome} salvo.`,
    onValido: (dados) =>
      g.item ? atualizar.mutateAsync({ id: g.item.id, ...dados }) : criar.mutateAsync(dados),
  })

  return (
    <section className="min-w-0">
      <TituloSecao titulo="Setores" onNovo={g.abrirNovo} />
      <Tabela>
        <Cabecalho>
          <Th>Nome</Th>
          <Th>Linha</Th>
          <Th>Postos</Th>
          <Th />
        </Cabecalho>
        <tbody>
          {lista.data?.map((s) => (
            <Linha key={s.id}>
              <Td className="font-medium">
                {s.nome} {!s.ativo && <Ativo ativo={false} />}
              </Td>
              <Td className="text-fg-muted">{s.linha?.nome ?? '—'}</Td>
              <Td numerico>{s._count.postos}</Td>
              <Td className="text-right">
                <BotaoEditar rotulo={s.nome} onClick={() => g.abrirEditar(s)} />
              </Td>
            </Linha>
          ))}
          {lista.isPending && <Carregando colunas={4} />}
          {lista.data?.length === 0 && <Vazio colunas={4}>Nenhum setor.</Vazio>}
        </tbody>
      </Tabela>
      <Gaveta
        aberta={g.aberta}
        titulo={g.item ? 'Editar setor' : 'Novo setor'}
        onFechar={g.fechar}
        rodape={
          <RodapeGaveta
            form="form-setor"
            salvando={criar.isPending || atualizar.isPending}
            onCancelar={g.fechar}
          />
        }
      >
        <Formulario id="form-setor" onSubmit={submit} erros={g.erros} onLimparErro={g.limparErro}>
          <Campo label="Nome" erro={g.erros.nome}>
            {(p) => <Input {...p} name="nome" defaultValue={g.item?.nome} autoFocus />}
          </Campo>
          <Campo label="Linha" erro={g.erros.linhaId}>
            {(p) => (
              <Select {...p} name="linhaId" defaultValue={g.item?.linhaId ?? ''}>
                <option value="">Sem linha</option>
                {linhas.data?.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.nome}
                  </option>
                ))}
              </Select>
            )}
          </Campo>
          <Campo label="Ordem de exibição" erro={g.erros.ordem}>
            {(p) => (
              <Input {...p} name="ordem" type="number" min={0} defaultValue={g.item?.ordem ?? 0} />
            )}
          </Campo>
          <Checkbox name="ativo" label="Ativo" defaultChecked={g.item?.ativo ?? true} />
        </Formulario>
      </Gaveta>
    </section>
  )
}

interface PostoItem {
  id: string
  codigo: string
  nome: string
  setorId: string
  ordem: number
  ativo: boolean
}

function Postos() {
  const trpc = useTRPC()
  const qc = useQueryClient()
  const lista = useQuery(trpc.cadastros.postos.listar.queryOptions())
  const setores = useQuery(trpc.cadastros.setores.listar.queryOptions())
  const dispositivos = useQuery(trpc.dispositivos.listar.queryOptions({}))
  const tabletsPorPosto = new Map<string, number>()
  for (const d of dispositivos.data ?? []) {
    tabletsPorPosto.set(d.postoId, (tabletsPorPosto.get(d.postoId) ?? 0) + 1)
  }
  const [postoTablets, setPostoTablets] = useState<PostoItem | null>(null)
  const g = useGavetaCadastro<PostoItem>()
  const invalidar = () => qc.invalidateQueries({ queryKey: trpc.cadastros.pathKey() })
  const criar = useMutation(
    trpc.cadastros.postos.criar.mutationOptions({
      onSuccess: () => {
        void invalidar()
        g.fechar()
      },
    }),
  )
  const atualizar = useMutation(
    trpc.cadastros.postos.atualizar.mutationOptions({
      onSuccess: () => {
        void invalidar()
        g.fechar()
      },
    }),
  )

  const submit = aoSubmeter({
    schema: postoSchema,
    numericos: ['ordem'],
    booleanos: ['ativo'],
    setErros: g.setErros,
    sucesso: (d) => `Posto ${d.nome} salvo.`,
    onValido: (dados) =>
      g.item ? atualizar.mutateAsync({ id: g.item.id, ...dados }) : criar.mutateAsync(dados),
  })

  return (
    <section className="min-w-0 min-[1680px]:col-span-1 lg:col-span-2">
      <TituloSecao titulo="Postos (tablets)" onNovo={g.abrirNovo} />
      <Tabela>
        <Cabecalho>
          <Th>Código</Th>
          <Th>Nome</Th>
          <Th>Setor</Th>
          <Th>Tablets</Th>
          <Th />
        </Cabecalho>
        <tbody>
          {lista.data?.map((p) => (
            <Linha key={p.id}>
              <Td>
                <Link
                  to="/posto/$postoId"
                  params={{ postoId: p.id }}
                  className="font-mono text-xs hover:text-brand"
                  title="Abrir tablet deste posto"
                >
                  {p.codigo}
                </Link>
              </Td>
              <Td className="font-medium">
                {p.nome} {!p.ativo && <Ativo ativo={false} />}
              </Td>
              <Td className="text-fg-muted">
                {p.setor.nome}
                {p.setor.linha ? ` · ${p.setor.linha.nome}` : ''}
              </Td>
              <Td>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setPostoTablets(p)}
                  aria-label={`Tablets de ${p.nome}`}
                  className={tabletsPorPosto.get(p.id) ? '' : 'text-amarelo'}
                  title={tabletsPorPosto.get(p.id) ? undefined : 'Nenhum tablet vinculado'}
                >
                  <TabletSmartphone className="size-4" aria-hidden />
                  <span className="tabular">{tabletsPorPosto.get(p.id) ?? 0}</span>
                </Button>
              </Td>
              <Td className="text-right">
                <BotaoEditar rotulo={p.nome} onClick={() => g.abrirEditar(p)} />
              </Td>
            </Linha>
          ))}
          {lista.isPending && <Carregando colunas={5} />}
          {lista.data?.length === 0 && <Vazio colunas={5}>Nenhum posto.</Vazio>}
        </tbody>
      </Tabela>
      <Gaveta
        aberta={g.aberta}
        titulo={g.item ? 'Editar posto' : 'Novo posto'}
        onFechar={g.fechar}
        rodape={
          <RodapeGaveta
            form="form-posto"
            salvando={criar.isPending || atualizar.isPending}
            onCancelar={g.fechar}
          />
        }
      >
        <Formulario id="form-posto" onSubmit={submit} erros={g.erros} onLimparErro={g.limparErro}>
          <Campo label="Setor" erro={g.erros.setorId}>
            {(p) => (
              <Select {...p} name="setorId" defaultValue={g.item?.setorId ?? ''}>
                <option value="">Escolha…</option>
                {setores.data?.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.nome}
                  </option>
                ))}
              </Select>
            )}
          </Campo>
          <Campo
            label="Código"
            erro={g.erros.codigo}
            dica="Curto e único, ex.: FRQ-01. Fica no suporte do tablet."
          >
            {(p) => (
              <Input
                {...p}
                name="codigo"
                defaultValue={g.item?.codigo}
                className="font-mono uppercase"
              />
            )}
          </Campo>
          <Campo label="Nome" erro={g.erros.nome}>
            {(p) => <Input {...p} name="nome" defaultValue={g.item?.nome} />}
          </Campo>
          <Campo label="Ordem de exibição" erro={g.erros.ordem}>
            {(p) => (
              <Input {...p} name="ordem" type="number" min={0} defaultValue={g.item?.ordem ?? 0} />
            )}
          </Campo>
          <Checkbox name="ativo" label="Ativo" defaultChecked={g.item?.ativo ?? true} />
        </Formulario>
      </Gaveta>
      <TabletsDoPosto
        posto={postoTablets}
        aberta={postoTablets !== null}
        onFechar={() => setPostoTablets(null)}
      />
    </section>
  )
}
